import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Admin } from 'app/models/admin';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';

const AUTH_API = 'https://app.titv-store-api.com/auth/signin';

@Injectable({
  providedIn: 'root'
})
export class AdminsService {
  basePath = "https://app.titv-store-api.com/auth/signin";
  baseAdminpath = "https://app.titv-store-api.com/";
  
  constructor(
    private router: Router,
    private http: HttpClient
  ) { }
  
  private refreshData = new Subject<void>();
  
  get refreshNedeed() {
    return this.refreshData;
  }
  
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  // ✅ Récupérer le token depuis sessionStorage
  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  // ✅ Récupérer les headers avec token
  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    if (token && token.length > 0) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  // ✅ Décoder le JWT et vérifier l'expiration
  private isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payloadBase64 = token.split('.')[1];
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);
      const exp = payload.exp;
      if (!exp) return false; // pas d'expiration -> valide

      const now = Math.floor(Date.now() / 1000);
      return exp < now;
    } catch (e) {
      console.error('Token decode error', e);
      return true;
    }
  }

  // ✅ Vérifier si l'utilisateur est connecté ET token non expiré
  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token || token.length === 0) return false;
    return !this.isTokenExpired();
  }

  // ✅ Méthode publique pour vérifier l'expiration (utilisée dans le guard)
  isTokenExpiredPublic(): boolean {
    return this.isTokenExpired();
  }

  // ✅ Récupérer les infos de l'utilisateur
  getCurrentUser(): any {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        return JSON.parse(authUser);
      }
      return null;
    } catch {
      return null;
    }
  }

  // ✅ Déconnexion
  logout() {
    console.log('🚪 Déconnexion admin...');
    sessionStorage.removeItem('auth-user');
    this.router.navigate(['/login']);
  }

  // ✅ Login (inchangé)
  login(email: string, password: string): Observable<any> {
    return this.http.post(AUTH_API, { email, password }, { 
      headers: this.httpOptions.headers,
      observe: 'response'
    }).pipe(
      tap((response: HttpResponse<any>) => {
        const authHeader = response.headers.get('Authorization');
        let token = '';
        if (authHeader) {
          token = authHeader.startsWith('Bearer ') ? authHeader.substring(7) : authHeader;
        } else if (response.body && response.body.token) {
          token = response.body.token;
        }
        if (token) {
          const userData = { ...response.body, token: token };
          sessionStorage.setItem('auth-user', JSON.stringify(userData));
        }
      }),
      map((response: HttpResponse<any>) => response.body),
      catchError(this.handleError)
    );
  }

  // ✅ CRUD (inchangé)
  createAdmin(item: any): Observable<Admin> {
    const headers = this.getHeaders();
    return this.http.post<Admin>(this.baseAdminpath + 'admins/add', JSON.stringify(item), { headers })
      .pipe(tap(() => this.refreshData.next()), catchError(this.handleError));
  }

  getAdmin(id: number): Observable<Admin> {
    const headers = this.getHeaders();
    return this.http.get<Admin>(this.baseAdminpath + 'admins/' + id, { headers })
      .pipe(catchError(this.handleError));
  }

  getListAdmin(): Observable<Admin[]> {
    const headers = this.getHeaders();
    return this.http.get<Admin[]>(this.baseAdminpath + 'admins/all', { headers })
      .pipe(catchError(this.handleError));
  }

  updateAdmin(id: number, item: any): Observable<Admin> {
    const headers = this.getHeaders();
    return this.http.put<Admin>(this.baseAdminpath + 'admins/edit/' + id, JSON.stringify(item), { headers })
      .pipe(tap(() => this.refreshData.next()), catchError(this.handleError));
  }

  deleteAdmin(id: number): Observable<any> {
    const headers = this.getHeaders();
    return this.http.delete(this.baseAdminpath + 'admins/delete/' + id, { headers })
      .pipe(tap(() => this.refreshData.next()), catchError(this.handleError));
  }

  // ✅ Gestion des erreurs avec redirection auto sur 401
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 401) {
      errorMessage = 'Session expirée. Veuillez vous reconnecter.';
      // Redirection après 2 secondes
      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 2000);
    } else if (error.status === 403) {
      errorMessage = 'Accès interdit. Permissions insuffisantes.';
    } else if (error.status === 0) {
      errorMessage = 'Erreur réseau. Vérifiez votre connexion.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    return throwError(() => new Error(errorMessage));
  }

  debugSessionStorage(): void {
    console.log('🔍 DEBUG sessionStorage:', sessionStorage.getItem('auth-user'));
  }

  // Add this public method to your AdminsService class
public getTokenPublic(): string {
  return this.getToken();
}

}